import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';
import { FacebookIcon, InstagramIcon } from './SocialIcons';
import SlashList from './SlashList';
import logoWhite from '../assests/logo-white.png';

// phones_extra / emails_extra are stored as a JSON-encoded array; parse
// defensively so a fresh install or empty string just means "none set".
function parseList(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export default function Footer() {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    phone: '+603-2720-9719',
    email: 'daniel@sankyomt.my',
    address: 'B-28-19, Mercu Summer Suite, Jalan Cendana 8, off Jalan Sultan Ismail, 50250 Kuala Lumpur Malaysia',
    site_name: 'Sankyo Machine Tools Philippines Inc.',
  });

  useEffect(() => {
    API.get('/settings').then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const phones = [settings.phone, ...parseList(settings.phones_extra)].filter(Boolean);
  const emails = [settings.email, ...parseList(settings.emails_extra)].filter(Boolean);

  const columns = [
    [['/', 'home'], ['/promotional', 'promotional'], ['/company-info', 'companyInfo']],
    [['/products', 'products'], ['/brands', 'brands'], ['/clients', 'clients']],
    [['/careers', 'careers'], ['/contact', 'contactUs']],
  ];

  return (
    <footer className="bg-charcoal text-white mt-auto">
      <div className="w-[90%] max-w-7xl mx-auto pt-16 pb-12 lg:pt-20 lg:pb-16 grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-12 lg:gap-20">
        {/* Brand */}
        <div>
          <Link to="/" className="inline-block">
            <img src={logoWhite} alt="Sankyo Machine Tools" className="h-7 w-auto" />
          </Link>
          <p className="text-sm text-white/70 leading-relaxed mt-5">
            {t('footer.tagline')}
          </p>
          {(settings.facebook_url || settings.instagram_url) && (
            <div className="flex items-center gap-3 mt-6">
              {settings.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-white/30 text-white hover:bg-primary hover:border-primary transition-colors">
                  <FacebookIcon className="w-4 h-4" />
                </a>
              )}
              {settings.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-white/30 text-white hover:bg-primary hover:border-primary transition-colors">
                  <InstagramIcon className="w-4 h-4" />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
          {/* Site links */}
          {columns.map((col, i) => (
            <ul key={i} className="space-y-4">
              {col.map(([to, key]) => (
                <li key={to}>
                  <Link to={to} className="text-sm font-medium text-white hover:text-primary transition-colors">{t(`common.${key}`)}</Link>
                </li>
              ))}
            </ul>
          ))}

          {/* Contact */}
          <ul className="space-y-3 col-span-2 md:col-span-1">
            {phones.length > 0 && (
              <li className="flex items-start gap-2.5 text-sm text-white/70">
                <PhoneIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <SlashList values={phones} hrefPrefix="tel:" linkClassName="hover:text-primary transition-colors" />
              </li>
            )}
            {emails.length > 0 && (
              <li className="flex items-start gap-2.5 text-sm text-white/70">
                <EnvelopeIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <SlashList values={emails} hrefPrefix="mailto:" linkClassName="hover:text-primary transition-colors break-all" />
              </li>
            )}
            {settings.address && (
              <li className="flex items-start gap-2.5 text-sm text-white/70">
                <MapPinIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <span>{settings.address}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="w-[90%] max-w-7xl mx-auto border-t border-white/15 py-5 text-xs text-white/50 font-btn tracking-wide">
        Copyright © {new Date().getFullYear()} {settings.site_name}. {t('footer.allRightsReserved')}
      </div>
    </footer>
  );
}
